/**
 * ONE definition of "unbilled" for the whole clients feature — the words, the
 * link, and nothing else.
 *
 * The figure itself is `rollup.unbilled`, computed on the server by
 * `AgreementMetricsService`/`rollupMetrics`: basis-aware (a fee agreement's hours
 * are COST, never priced by the hour) and bank-aware (support hours a purchased
 * block already paid for are netted off). Nothing in the frontend re-derives
 * it, and this file must never start.
 *
 * ⚠ THE LIST AND THE DETAIL PAGE MUST SAY THE SAME SENTENCE. The tooltip below
 * lived as a private const in `ClientsPage.tsx`, which is part of why the
 * client detail page could go a whole release describing this money in no words
 * at all: the definition was somewhere the page could not reach.
 */

import { formatMoney } from '../../lib/format';

const TODAY_ISO = new Date().toLocaleDateString('en-CA');

/** Rollup precision: a total across a set is whole dollars (CLAUDE.md). */
const money = (v: number) => formatMoney(v, { whole: true });

export const UNBILLED_TITLE =
  'Everything delivered to this client that nobody has been asked to pay for: billable time on its HOURLY agreements with no prepaid support hours behind it, plus billable expenses no invoice has claimed. Hours Harvest already billed or locked are excluded — they are read-only here and may sit on a Harvest invoice this app never saw. Support hours already drawn from a purchased block are excluded — those are paid, and billing them again charges twice for one hour — and fee-agreement hours are cost, never priced by the hour. It is the SAME figure the invoice wizard offers, to the cent: /invoices/new totals this same definition, so the number you click is the number it bills. Click it to open the wizard on exactly the span this work covers.';

/**
 * ⚠ ONE SET OF HOURS, ONE PRICE — and the addition written down.
 *
 * A critic found Meridian's row printing `17 h over the bank · $2,805` under
 * the client name and `$2,985` in the Unbilled column, and read it as two
 * prices for one set of hours. It is not: $2,985 IS $2,805 of billable time
 * (those 17 h) plus $180 of billable expenses. Nothing on the row said so, so
 * the reader had to hold two figures and guess at the relationship.
 *
 * `unbilled − unbilledExpenses` is the time half exactly — `AgreementMetrics`
 * builds `unbilledTotal` as those two terms and nothing else — so this splits
 * a figure, it never re-derives one. Wherever both halves are shown, they are
 * shown as an addition that comes to the figure above them.
 */
export function unbilledSplit(unbilled: number, expenses: number): string | null {
  if (!(expenses > 0) || !(unbilled > expenses)) return null;
  return `${money(unbilled - expenses)} time · ${money(expenses)} expenses`;
}

export const UNBILLED_SPLIT_TITLE =
  'What the Unbilled figure is made of: billable time at the rates it will actually be invoiced at, plus billable expenses no invoice has claimed. They add up to the figure above — there is one price for the hours, and it is the time half.';

export const PREPAID_TITLE =
  'Uninvoiced support work this client’s prepaid block has ALREADY paid for, taken straight from the bank’s own consumption statement. It is excluded from Unbilled and must not be invoiced again — the money arrived when they bought the hours.';

/**
 * The invoice wizard, opened on a window that actually contains the money the
 * caller names. A deep link carrying only a client id lands on the picker, so a
 * button would name a figure and then make you find it again.
 *
 * `from` is the oldest entry that money is made of — on a support client, the
 * oldest hour NO purchased block covered.
 */
export function unbilledWizardHref(
  clientId: string,
  from: string | null,
  clientName?: string,
): string {
  const base = `/invoices/new?tab=unbilled&clientId=${encodeURIComponent(clientId)}`;
  const source = clientName ? `&source=${encodeURIComponent(clientName)}` : '';
  return from ? `${base}&from=${from}&to=${TODAY_ISO}${source}` : `${base}${source}`;
}
